import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid2,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  PersonOutline,
  Email,
  Business,
  SettingsOutlined as SettingsOutlinedIcon,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import NavigationRail from "../common-components/NavigationRail";
import {
  getUserByEmail,
  getAllProjectsByAdmin,
  changePassword,
  logout,
} from "../../services/webServices";

// Styled components following existing patterns
const GradientTypography = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(94deg, #0074ff 11.09%, #e24e5f 99.55%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
}));

const ProfilePage = () => {
  const navigate = useNavigate();
  const [displayUserName, setUserDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [assignedProject, setAssignedProject] = useState("");
  const [role, setRole] = useState("");
  const [designation, setDesignation] = useState("");
  const [userId, setUserId] = useState("");

  // Password change states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const fetchUserData = async () => {
    try {
      // Fetch user details using getUserByEmail API
      const userResponse = await getUserByEmail();

      if (
        userResponse?.data?.data &&
        Array.isArray(userResponse.data.data) &&
        userResponse?.data?.data?.length > 0
      ) {
        const userData = userResponse?.data?.data[0]; // Get first user from array

        // Set user ID
        if (userData.id) {
          setUserId(userData.id);
        }

        // Set username/display name - prioritize display_name over username
        if (userData.display_name) {
          setUserDisplayName(userData.display_name);
        } else if (userData.username) {
          setUserDisplayName(userData.username);
        }

        // Set email from username (since username contains the email)
        if (userData.username) {
          setEmail(userData.username);
        }

        // Fetch project details if project_id exists
        if (userData.project_id) {
          try {
            const projectsResponse = await getAllProjectsByAdmin();
            if (
              projectsResponse?.data &&
              Array.isArray(projectsResponse.data)
            ) {
              const project = projectsResponse.data.find(
                (p) => p.project_id === userData.project_id
              );
              if (project) {
                setAssignedProject(
                  project.project_name || `Project ID: ${userData.project_id}`
                );
              } else {
                setAssignedProject(`Project ID: ${userData.project_id}`);
              }
            } else {
              setAssignedProject(`Project ID: ${userData.project_id}`);
            }
          } catch (projectError) {
            console.error("Error fetching project details:", projectError);
            setAssignedProject(`Project ID: ${userData.project_id}`);
          }
        }
      } else {
        console.warn("No user data found in response");
        // Set default values if no data
        setEmail("-");
        setUserDisplayName("-");
        setAssignedProject("-");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Set default values on error
      setEmail("-");
      setUserDisplayName("-");
      setAssignedProject("-");
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const handlePasswordChange = async () => {
    // Reset messages
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    // Password strength validation
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setPasswordError(
        "Password must include uppercase, lowercase, numbers, and special characters"
      );
      return;
    }

    if (!userId) {
      setPasswordError(
        "User ID not found. Please refresh the page and try again."
      );
      return;
    }

    try {
      // Call the changePassword API
      const response = await changePassword({
        currentPassword,
        newPassword,
        userId,
      });

      if (response.data) {
        setPasswordSuccess("Password changed successfully!");

        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        // Show success popup
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error("Password change error:", error);
      if (error.response?.data?.message) {
        setPasswordError(error.response.data.message);
      } else {
        setPasswordError("Failed to change password. Please try again.");
      }
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
    navigate("/landingpage");
  };

  const handleSuccessPopupOK = async () => {
    try {
      await logout();
      navigate("/");
    } catch (logoutError) {
      console.error("Logout error:", logoutError);
      // Force redirect even if logout fails
      navigate("/");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "#F6F3F2",
      }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexWrap: "nowrap",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* NavigationRail Section */}
        <Grid2
          item
          xs={12}
          md={2}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          <NavigationRail />
        </Grid2>

        {/* Main Content */}
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(FFFFFF, #f4f4f4)",
            maxWidth: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 6,
            px: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 6, maxWidth: "600px" }}>
            <GradientTypography
              variant="h3"
              component="h1"
              sx={{
                mb: 2,
                fontWeight: 400,
                fontSize: { xs: "32px", md: "48px" },
                lineHeight: "56px",
              }}
            >
              My Profile
            </GradientTypography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "14px", md: "16px" },
                color: "text.secondary",
              }}
            >
              Manage your account information and security settings
            </Typography>
          </Box>

          {/* Profile Information Card */}
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              width: "100%",
              maxWidth: "600px",
              mb: 2,
              backgroundColor: "#f0f4f8",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Profile Information
            </Typography>

            {/* Username Section */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#e3f2fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  border: "2px solid #1976d2",
                }}
              >
                <PersonOutline sx={{ color: "#1976d2", fontSize: 24 }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Username
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {displayUserName || "johndoe"}
                </Typography>
              </Box>
            </Box>

            {/* Email Section */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#e8f5e8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  border: "2px solid #2e7d32",
                }}
              >
                <Email sx={{ color: "#2e7d32", fontSize: 24 }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Email Address
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {email}
                </Typography>
              </Box>
            </Box>

            {/* Assigned Project Section */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#f3e5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  border: "2px solid #7b1fa2",
                }}
              >
                <Business sx={{ color: "#7b1fa2", fontSize: 24 }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Assigned Project
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {assignedProject}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Change Password Section */}
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              width: "100%",
              maxWidth: "600px",
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <LockOutlined sx={{ color: "#ff9800", fontSize: 28, mr: 2 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Change Password
              </Typography>
            </Box>

            {/* Password Fields */}
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#e3f2fd",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Password Requirements:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" color="#1976d2" sx={{ mb: 1 }}>
                  At least 8 characters long
                </Typography>
                <Typography component="li" color="#1976d2" sx={{ mb: 1 }}>
                  Include uppercase and lowercase letters
                </Typography>
                <Typography component="li" color="#1976d2" sx={{ mb: 1 }}>
                  Include at least one number
                </Typography>
                <Typography component="li" color="#1976d2">
                  Include at least one special character
                </Typography>
              </Box>
            </Box>

            {/* Error/Success Messages */}
            {passwordError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {passwordSuccess}
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePasswordChange}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "16px",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                Change Password
              </Button>
            </Box>
          </Paper>
        </Box>
      </Grid2>

      {/* Success Popup Dialog */}
      <Dialog
        open={showSuccessPopup}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: "400px",
            padding: "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            paddingBottom: "10px",
            fontFamily: "Roboto, sans-serif",
            textAlign: "center",
            color: "#2e7d32",
          }}
        >
          Password Changed Successfully!
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: "20px", textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "16px",
              fontFamily: "Roboto, sans-serif",
              color: "#333",
            }}
          >
            Your password has been updated successfully. You will be logged out
            for security reasons.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", padding: "10px 24px" }}>
          <Button
            variant="contained"
            onClick={handleSuccessPopupOK}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "16px",
              backgroundColor: "#2e7d32",
              "&:hover": {
                backgroundColor: "#1b5e20",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
